/**
 * useInvestorAuth Hook
 * 
 * Manages investor authentication state using the investor.me tRPC endpoint.
 * Handles redirect to /investor/login when unauthenticated.
 */
import { trpc } from "@/lib/trpc";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "wouter";

type UseInvestorAuthOptions = {
  redirectOnUnauthenticated?: boolean;
};

export function useInvestorAuth(options?: UseInvestorAuthOptions) {
  const { redirectOnUnauthenticated = false } = options ?? {};
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  const meQuery = trpc.investor.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logoutMutation = trpc.investor.logout.useMutation({
    onSuccess: () => {
      utils.investor.me.setData(undefined, null);
      setLocation("/investor/login");
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      utils.investor.me.setData(undefined, null);
      await utils.investor.me.invalidate();
    }
  }, [logoutMutation, utils]);

  const state = useMemo(() => {
    return {
      user: meQuery.data ?? null,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(meQuery.data),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/investor/login") return;
    setLocation("/investor/login");
  }, [
    redirectOnUnauthenticated,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
    setLocation,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
